// ============================================================================
// BehaviorIncidentsByUnit.tsx
// Section component for displaying behavior incidents grouped by nursing unit
// ============================================================================

import { Bar } from "react-chartjs-2";
import { Fragment, useEffect, useState } from "react";
import { Container, Child, Header, LayerContent } from "../styles";
import { ChartContainer, NoData } from "../../style";
import { useAnalyticsWrapper } from "../../../../../store/store";
import {
  barOptions,
  handlePointClicked,
} from "../formUtils";
import SectionModal from "./BehaviorAnalysisSectionModal";
import {
  handleCSVDownload,
  handlePDFDownload,
} from "../../../../../utils/helpers/pdfCsvUtils";
import { behaviorAnalysisDetailCols } from "../../dummyData/colums";
import { Grid, GridColumn, GridColumnProps } from "@progress/kendo-react-grid";
import {
  CenteredWholeNumCell,
  HeaderCenteredCell,
} from "../../../../../components/DynamicGrid/customCells";
import { TotalRow } from "../../../../../components/DynamicGrid/customRows";
import useFormDataHelpers from "../../../../../hooks/useFormDataHelpers";
import { MITIGATE_COLORS } from "../../../../../shared/sharedConstants";

// ============================================================================
// Props Interface
// ============================================================================
interface BehaviorIncidentsByUnitProps {
  setClickedIncident: (item: any) => void;
  isFetchingSelectedIncident: boolean;
  shareTableData?: (data: any[]) => void;
}

// ============================================================================
// Row Type Definition - unit, count, and percentage
// ============================================================================
type UnitRow = {
  unit: string;
  count: number;
  percentage: number;
};

// ============================================================================
// API Response Types
// ============================================================================
interface IncidentItem {
  id: string;
  incidentNumber: string;
  residentName: string;
  unit: string;
  facilityDate: string;
  facilityTime: string;
  location: string;
  behaviorType: string;
  injury: string;
  [key: string]: string;
}

type UnitDrilldown = Record<string, IncidentItem[]>;

// ============================================================================
// Component
// ============================================================================
const BehaviorIncidentsByUnit = ({
  setClickedIncident,
  isFetchingSelectedIncident,
  shareTableData,
}: BehaviorIncidentsByUnitProps) => {
  // ---------------------------------------------------------------------------
  // Helper Hooks
  // ---------------------------------------------------------------------------
  const { getFacilityLabel } = useFormDataHelpers();

  // ---------------------------------------------------------------------------
  // Local State
  // ---------------------------------------------------------------------------
  const [tableData, setTableData] = useState<UnitRow[]>([]);
  const [grid, setGrid] = useState<any>([]);
  const [barData, setBarData] = useState<any>([]);
  const [modalData, setModalData] = useState<any>([]);
  const [unit, setUnit] = useState("");
  const [showUnitModal, setShowUnitModal] = useState(false);

  // ---------------------------------------------------------------------------
  // Global State from Zustand
  // ---------------------------------------------------------------------------
  const { startDate, endDate, behaviorFacility, behaviorAnalysisIncidents } =
    useAnalyticsWrapper((state) => ({
      startDate: state.startDate,
      endDate: state.endDate,
      behaviorFacility: state.behaviorFacility,
      behaviorAnalysisIncidents: state.behaviorAnalysisIncidents,
    }));

  // Derived value for facility name
  const facilityLabel = getFacilityLabel(behaviorFacility);

  // ---------------------------------------------------------------------------
  // Data Transformation Function
  // ---------------------------------------------------------------------------
  const adjustedTableData = (unitDrilldown: UnitDrilldown): UnitRow[] => {
    const returnData: UnitRow[] = [];

    if (!unitDrilldown || Object.keys(unitDrilldown).length === 0) {
      return [];
    }

    let totalIncidents = 0;

    // First pass: calculate total incidents
    Object.entries(unitDrilldown).forEach(([, incidents]) => {
      totalIncidents += incidents.length;
    });

    // Second pass: transform each unit with percentage
    Object.entries(unitDrilldown).forEach(([unitName, incidents]) => {
      const count = incidents.length;
      const percentage = totalIncidents > 0 
        ? Math.round((count / totalIncidents) * 10000) / 100 
        : 0;
      
      const row: UnitRow = {
        unit: unitName,
        count: count,
        percentage: percentage,
      };

      returnData.push(row);
    });

    // Add totals row
    const totalRow: UnitRow = {
      unit: "Total",
      count: totalIncidents,
      percentage: 100,
    };
    returnData.push(totalRow);

    return returnData;
  };

  // ---------------------------------------------------------------------------
  // Transform table data for bar chart (ensures chart order matches table order)
  // ---------------------------------------------------------------------------
  const transformTableDataForChart = (rows: UnitRow[]) => {
    // Filter out the totals row for the chart
    const dataRows = rows.filter(row => row.unit !== "Total");
    
    if (dataRows.length === 0) {
      return { labels: [], datasets: [] };
    }

    const labels = dataRows.map(row => row.unit);
    const data = dataRows.map(row => row.count);

    // Alternating colors from MITIGATE_COLORS
    const alternatingColors = [
      MITIGATE_COLORS.BLUE[0],
      MITIGATE_COLORS.GREEN[0],
      MITIGATE_COLORS.RED[0],
      MITIGATE_COLORS.ORANGE[0],
      MITIGATE_COLORS.PURPLE[0],
      MITIGATE_COLORS.BLUE[1],
      MITIGATE_COLORS.GREEN[1],
      MITIGATE_COLORS.RED[1],
      MITIGATE_COLORS.ORANGE[1],
      MITIGATE_COLORS.PURPLE[1],
      MITIGATE_COLORS.BLUE[2],
      MITIGATE_COLORS.GREEN[2],
      MITIGATE_COLORS.RED[2],
      MITIGATE_COLORS.ORANGE[2],
      MITIGATE_COLORS.PURPLE[2],
    ];

    return {
      labels,
      datasets: [
        {
          label: "# of Incidents",
          data,
          backgroundColor: alternatingColors.slice(0, labels.length),
          borderWidth: 0,
        },
      ],
    };
  };

  // ---------------------------------------------------------------------------
  // Process data when behaviorAnalysisIncidents changes
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (behaviorAnalysisIncidents !== undefined) {
      const tempData = behaviorAnalysisIncidents?.incidentsByUnitSection?.incidentsByUnitDrilldown;
      const transformedData = adjustedTableData(tempData);
      const transformedBarData = transformTableDataForChart(transformedData);

      setTableData(transformedData);
      setBarData(transformedBarData);
      shareTableData?.(transformedData);
    }
  }, [behaviorAnalysisIncidents]);

  const title = "Behavior Incidents by Unit";

  // ---------------------------------------------------------------------------
  // Handle clicking a bar in the chart
  // ---------------------------------------------------------------------------
  const handleBarClicked = (chart: any) => {
    if (!chart.length) return;
    const label = handlePointClicked(chart, barData?.labels);
    setShowUnitModal(true);
    setUnit(label);
    // Get drilldown data for the clicked unit from incidentsByUnitSection
    const drilldownData = behaviorAnalysisIncidents?.incidentsByUnitSection?.incidentsByUnitDrilldown || {};
    const filteredDetails = drilldownData[label] || [];
    setModalData(filteredDetails);
  };

  // ---------------------------------------------------------------------------
  // Build Kendo Grid when tableData changes
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const cellCountProps = {
      cells: { data: CenteredWholeNumCell, headerCell: HeaderCenteredCell },
    };
    const cellPercentageProps = {
      cells: { data: CenteredWholeNumCell, append: '%', headerCell: HeaderCenteredCell },
    };

    const grid = (
      <Fragment>
        <Grid
          data={tableData || []}
          scrollable="none"
          rows={{ data: TotalRow }}
        >
          <GridColumn field="unit" title="Unit" />
          <GridColumn
            field="count"
            title="# of Incidents"
            {...(cellCountProps as GridColumnProps)}
          />
          <GridColumn
            field="percentage"
            title="%"
            {...(cellPercentageProps as GridColumnProps)}
          />
        </Grid>
      </Fragment>
    );
    setGrid(grid);
  }, [tableData]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <LayerContent>
      <Header>{title}</Header>
      <Container>
        <Child flex="40%">
          {grid}
        </Child>
        <Child flex="60%">
          <ChartContainer>
            {barData?.labels?.length > 0 ? (
              <Bar options={barOptions(handleBarClicked)} data={barData} />
            ) : (
              <NoData>No Data</NoData>
            )}
          </ChartContainer>
        </Child>
      </Container>

      {showUnitModal && (
        <SectionModal
          open={showUnitModal}
          facilityName={facilityLabel}
          startDate={startDate}
          endDate={endDate}
          closeFn={() => setShowUnitModal(false)}
          resetFn={() => {}}
          isLoading={false}
          tableData={modalData}
          tableCols={behaviorAnalysisDetailCols()}
          title={`${title} - ${unit}`}
          getUserCallback={(item: any) => setClickedIncident(item)}
          handlePdfExport={() =>
            handlePDFDownload({
              data: modalData,
              columnData: behaviorAnalysisDetailCols(),
              fileName: facilityLabel,
              startDate,
              endDate,
              subTitle: `${title} - ${unit}`,
            })
          }
          handleCsvExport={() =>
            handleCSVDownload(
              modalData,
              behaviorAnalysisDetailCols(),
              `${facilityLabel} - ${title} - ${unit}`
            )
          }
        />
      )}
    </LayerContent>
  );
};

export default BehaviorIncidentsByUnit;
